import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Sizes } from '@common/types';
import { getSelectedDateRangeString } from '@common/utils';
import { Box, Modal, PillGroup, SectionTitle } from '@base-app';
import { DEFAULT_PAGE_SIZE } from '@components/constants';
import { COMMON_TRANSLATION } from '@constants/index';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { useQuerySampleTrackingStrategy, UseSampleTrackingRequest } from '@modules/study-insights/strategies';
import { SampleTrackingStatus } from '@modules/study-insights/types';
import { useSequenceSamplesStore } from '@modules/study-insights/store';
import { ViewOrderIdsModalProps } from './ViewOrderIdsModal.types';
import { ViewOrderIdsParams } from '../ViewOrderIds.types';
import { StyledDate, StyledHeaderFilter, StyledSelectedRange } from './ViewOrderIdsModal.styles';
import { Pill, PillId } from '../../../CollectionAndSequencing.types';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';
import { PartnerOrganization } from '@/modules/core/types';

import ViewOrderIds from '../ViewOrderIds';

const ViewOrderIdsModal: FC<ViewOrderIdsModalProps> = ({ collectionLocation, isOpen, onClose, title }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [params, setParams] = useState<ViewOrderIdsParams>({
    limit: DEFAULT_PAGE_SIZE,
  });
  const [pills, setPills] = useState<Pill[]>([]);
  const [filterPill, setFilterPill] = useState<string>();
  const { selectedPill: sequenceSamplesPill } = useSequenceSamplesStore();

  const organizationName = useOrganizationName();
  const props: UseSampleTrackingRequest = {
    partner: organizationName as PartnerOrganization,
    params: {
      offset: params.offset,
      limit: params.limit,
      start: params.from,
      end: params.to,
      status: params.status,
      sampleType: params.sampleType,
      collectionLocation,
      query: params.query,
      sort: params.sort,
      retrospective: true,
    },
    options: {
      enabled: params?.selectedDateRange !== undefined && params?.status !== undefined,
    },
  };

  const {
    data: sampleTrackingData,
    isError: isSampleTrackingError,
    isFetching: isSampleTrackingFetching,
    isLoading: isSampleTrackingLoading,
  } = useQuerySampleTrackingStrategy(props);

  const handleClose = (): void => {
    onClose?.();
  };

  const handleChangeParams = (changedParams: ViewOrderIdsParams): void => {
    setParams({
      ...params,
      ...changedParams,
    });
  };

  const handleOnClickPill = (id: string): void => {
    setFilterPill(id);
  };

  const getSelectedData = (): string => {
    const startDate = params?.from;
    const endDate = params?.to;

    if (startDate === undefined || endDate === undefined) {
      return t('date_range_dropdown_options.all_time', { ns: COMMON_TRANSLATION });
    }

    return getSelectedDateRangeString(startDate as Date, endDate);
  };

  useEffect(() => {
    const pillOptions = [
      {
        id: 'sequenced_samples',
        label: t('view_order_id.status.sequenced_samples'),
      },
      {
        id: 'recollect',
        label: t('view_order_id.status.recollect'),
      },
    ];
    switch (sequenceSamplesPill) {
      case PillId.SEQUENCED:
        setPills(pillOptions);
        setFilterPill(pillOptions[0].id);
        break;
      case PillId.RECEIVED:
        setParams({
          ...params,
          status: [SampleTrackingStatus.RECEIVED],
        });
        break;
      case PillId.AWAITING:
        setParams({
          ...params,
          status: [SampleTrackingStatus.AWAITING],
        });
        break;
    }
  }, []);

  useEffect(() => {
    if (!filterPill) {
      return;
    }

    if (sequenceSamplesPill === PillId.SEQUENCED) {
      let statusFilter: SampleTrackingStatus[] = [];
      switch (filterPill) {
        case 'sequenced_samples':
          statusFilter = [SampleTrackingStatus.SEQUENCING_COMPLETE];
          break;
        case 'recollect':
          statusFilter = [SampleTrackingStatus.RECOLLECT];
      }

      setParams({
        ...params,
        status: statusFilter,
      });
    }
  }, [filterPill]);

  return (
    <Modal
      title={
        <SectionTitle css={{ color: '$darkBlue200', textAlign: 'left' }}>
          <Box as="span" css={{ fontWeight: '$bold' }}>
            {t('view_order_id.patient_order_id', { ns: STUDY_INSIGHTS_TRANSLATION })}
          </Box>
          &nbsp;
          <Box as="span" css={{ fontWeight: '$regular' }}>
            {title}
          </Box>
        </SectionTitle>
      }
      onClose={handleClose}
      show={isOpen}
      size={Sizes.XXX_LARGE}
    >
      <>
        <StyledHeaderFilter>
          <Box>
            {filterPill && !!pills.length && (
              <PillGroup items={pills} onClickPill={handleOnClickPill} selectedPill={filterPill} />
            )}
          </Box>
          {(sequenceSamplesPill === PillId.SEQUENCED || sequenceSamplesPill === PillId.RECEIVED) && (
            <StyledSelectedRange data-testid="view-order-ids-date-range">
              <Box>{t('selected_date_range.label')}</Box>
              <StyledDate> {getSelectedData()} </StyledDate>
            </StyledSelectedRange>
          )}
        </StyledHeaderFilter>
        <ViewOrderIds
          collectionLocation={collectionLocation}
          data={sampleTrackingData}
          isError={isSampleTrackingError}
          isLoading={isSampleTrackingLoading || isSampleTrackingFetching}
          onChangeParams={handleChangeParams}
        />
      </>
    </Modal>
  );
};

export default ViewOrderIdsModal;
